import { Box, Typography } from '@mui/material'
import ReportElementFloatingLegend from 'components/ReportElement/ReportElementFloatingLegend'
import ReportElementSettings, {
  ReportElementSettingsProps,
} from 'components/ReportElement/ReportElementSettings'
import React from 'react'

type ReportElementTablePrintProps = {
  rows: any[]
  cols: any[]
  elementTitle?: string
  legendItems?: React.ComponentProps<
    typeof ReportElementFloatingLegend
  >['items']
} & ReportElementSettingsProps

type PrintCellStyle = React.CSSProperties & {
  printColorAdjust?: 'exact'
  WebkitPrintColorAdjust?: 'exact'
}

const ReportElementTablePrint: React.FC<ReportElementTablePrintProps> = ({
  rows,
  cols,
  elementTitle,
  legendItems = [],
  print,
  reportId,
  element,
  sectionId,
  report,
  noSubgroup,
  noCohort,
  noPastResults,
  showHideOptions,
  hidden = [],
  onChangeHidden,
  onExport,
  favorites,
  onUpdateFavorites,
}) => {
  const thStyle = (index: number, total: number): React.CSSProperties => {
    return {
      textAlign: 'left',
      width: index === 0 ? '240px' : undefined,
      paddingLeft: index === 0 ? '0px' : '16px',
      paddingRight: index === total - 1 ? '0px' : '16px',
      paddingBottom: '16px',
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
    }
  }

  const flippedThStyle = (
    index: number,
    total: number
  ): React.CSSProperties => {
    return {
      ...thStyle(index, total),
      fontWeight: 'normal',
    }
  }

  const tdStyle = (index: number, total: number): React.CSSProperties => {
    return {
      textAlign: 'left',
      width: index === 0 ? '240px' : undefined,
      paddingLeft: index === 0 ? '0px' : '16px',
      paddingRight: index === total - 1 ? '0px' : '16px',
      paddingBottom: '16px',
      paddingTop: '16px',
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
    }
  }

  const flippedTdStyle = (
    index: number,
    total: number
  ): React.CSSProperties => {
    return {
      ...tdStyle(index, total),
      fontWeight: 'bold',
    }
  }

  const getCellColorStyle = (col: any, row: any): PrintCellStyle => {
    const colorStyle =
      typeof col.color === 'function'
        ? col.color(row)
        : typeof col.color === 'string'
          ? { backgroundColor: col.color }
          : col.color

    if (!colorStyle || Object.keys(colorStyle).length === 0) {
      return {}
    }

    return {
      ...colorStyle,
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
    }
  }

  const renderHtmlContent = (content: React.ReactNode): React.ReactNode => {
    if (typeof content === 'string') {
      return (
        <Box component="div" dangerouslySetInnerHTML={{ __html: content }} />
      )
    }

    return content
  }

  const shownColumns = cols.filter(
    (c) => !hidden.includes(c.title) && !hidden.includes(c.key)
  )

  const renderLegend = () =>
    legendItems.length > 0 ? (
      <Box mt={2} pl={2} sx={{ breakInside: 'avoid' }}>
        <ReportElementFloatingLegend
          removeAxisPadding
          items={legendItems}
          hidden={hidden}
          opacity={0.08}
          swatchWidth={60}
          swatchHeight={30}
        />
      </Box>
    ) : null

  if (shownColumns.length > 6) {
    const rowsToUse = shownColumns
    const colsToUse = rows
    const rowTitle = rowsToUse[0]
    return (
      <>
        <table
          style={{
            tableLayout: 'fixed',
            width: '100%',
            marginBottom: 4,
            breakInside: 'avoid',
          }}
        >
          <thead style={{ breakInside: 'avoid' }}>
            <tr style={{ breakInside: 'avoid' }}>
              <th colSpan={colsToUse.length + 1} style={{ textAlign: 'left' }}>
                <Box my={4}>
                  {elementTitle && (
                    <Typography variant="h5">{elementTitle}</Typography>
                  )}
                </Box>
                <Box mb={4}>
                  <ReportElementSettings
                    print={true}
                    reportId={reportId}
                    element={element}
                    sectionId={sectionId}
                    report={report}
                    noSubgroup={noSubgroup}
                    noCohort={noCohort}
                    noPastResults={noPastResults}
                    showHideOptions={showHideOptions}
                    hidden={hidden}
                    onChangeHidden={onChangeHidden}
                    onExport={onExport}
                    favorites={favorites}
                    onUpdateFavorites={onUpdateFavorites}
                  />
                </Box>
              </th>
            </tr>
            <tr style={{ breakInside: 'avoid' }}>
              <th
                colSpan={colsToUse.length + 1}
                style={thStyle(0, colsToUse.length)}
              >
                {renderHtmlContent(rowTitle.title)}
              </th>
            </tr>
            <tr style={{ breakInside: 'avoid' }}>
              <th style={thStyle(0, colsToUse.length + 1)}></th>
              {colsToUse.map((col, colIndex) => {
                return (
                  <th
                    key={col.category}
                    style={flippedThStyle(colIndex + 1, colsToUse.length)}
                  >
                    {renderHtmlContent(col.category)}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rowsToUse.map((col, i) => {
              if (i === 0) {
                return null
              }
              return (
                <tr key={i} style={{ breakInside: 'avoid' }}>
                  <td style={flippedTdStyle(0, colsToUse.length + 1)}>
                    {renderHtmlContent(col.title)}
                  </td>
                  {colsToUse.map((row, rowIndex) => {
                    return (
                      <td
                        style={{
                          ...tdStyle(rowIndex + 1, colsToUse.length),
                          ...getCellColorStyle(col, row),
                        }}
                      >
                        {col.render ? col.render(row) : col[row.key]}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
        {renderLegend()}
      </>
    )
  }
  return (
    <>
      <table
        style={{
          tableLayout: 'fixed',
          width: '100%',
          marginBottom: 4,
          breakInside: 'avoid',
        }}
      >
        <thead style={{ breakInside: 'avoid' }}>
          <tr style={{ breakInside: 'avoid' }}>
            <th colSpan={shownColumns.length} style={{ textAlign: 'left' }}>
              <Box my={4}>
                {elementTitle && (
                  <Typography variant="h5">{elementTitle}</Typography>
                )}
              </Box>
              <Box mb={4}>
                <ReportElementSettings
                  print={true}
                  reportId={reportId}
                  element={element}
                  sectionId={sectionId}
                  report={report}
                  noSubgroup={noSubgroup}
                  noCohort={noCohort}
                  noPastResults={noPastResults}
                  showHideOptions={showHideOptions}
                  hidden={hidden}
                  onChangeHidden={onChangeHidden}
                  onExport={onExport}
                  favorites={favorites}
                  onUpdateFavorites={onUpdateFavorites}
                />
              </Box>
            </th>
          </tr>
          <tr style={{ breakInside: 'avoid' }}>
            {shownColumns.map((col, colIndex) => {
              return (
                <th
                  key={col.key}
                  style={thStyle(colIndex, shownColumns.length)}
                >
                  {renderHtmlContent(col.title || col.key)}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            return (
              <tr key={rowIndex} style={{ breakInside: 'avoid' }}>
                {shownColumns.map((col, colIndex) => {
                  return (
                    <td
                      key={`${rowIndex}:${col.key}`}
                      style={{
                        ...tdStyle(colIndex, shownColumns.length),
                        ...getCellColorStyle(col, row),
                      }}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      {renderLegend()}
    </>
  )
}

export default ReportElementTablePrint
